import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row, Skeleton } from 'antd'
//
import global from 'common'
import { BaseComponentProps } from 'employeeSignup/common/models'
import { Formik } from 'formik'
import * as Yup from 'yup'

import finalizePerksBenefitDesign from 'core/application/adminDashboard/benefitDetails/design/perks/finalizeBenefit'
import { getPerksProductClassDesignWizard } from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getPerksProductClassDesignWizard'
import { getPerksProductClassDesignWizardState } from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getPerksProductClassDesignWizardState'
import removePerksProductClass from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/removeProductClass'
import getBenefitProductClasses, {
  BenefitProductClassResponse,
} from 'core/application/adminDashboard/benefitDetails/getBenefitProductClasses'
import { WizardStatus } from 'core/domain/wizard/wizard'

import Modal from 'common/components/feedback/Modal'

import ProductClassDetailsOnBenefitDesign from '../../../common/productClassDetails/productClassDetailsOnBenefitDesign'
import ProductClassWizardModal from '../../../common/productClassWizardModal'
import {
  PerksProductClassWizard,
  PerksProductClassWizardNavItemsMapping,
} from '../employeeClasses/productClassWizard/ProductClassStepsMapping'
import FinishDesignSuccessModal from './components/FinishDesignSuccessModal'

//
import './styles.less'

export type PerksFinishBenefitDesignProps = {
  planPeriodId: string
  wizardStatus: WizardStatus
}

type ProductClassModalStateProps = {
  productId?: string
  visible: boolean
  editingStep?: string
  name?: string
}

const PerksFinishBenefitDesign: React.FC<BaseComponentProps<PerksFinishBenefitDesignProps>> = (props) => {
  const { t: translate } = useTranslation()

  const [productClasses, areProductClassesLoading, , reloadProductClasses] = global.hooks.useQueryRequest<
    BenefitProductClassResponse[]
  >(null, () => getBenefitProductClasses(props.state.planPeriodId))
  const [deleteProductClass, setDeleteProductClass] = useState<BenefitProductClassResponse | null>(null)
  const [sendDeleteProductClassCommand, isSendDeleteProductClassCommandLoading] = global.hooks.useCommandRequest()
  const [productClassModalState, setProductClassModalState] = useState<ProductClassModalStateProps>({
    visible: false,
  })
  const [postBenefitDesignCommand, postBenefitDesignCommandLoading] = global.hooks.useCommandRequest()

  const { bindSubmitFormHandler } = props
  const [updateSummary, setUpdateSummary] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)

  useEffect(() => {
    if (!productClasses || productClasses.length == 0) {
      props.setIsFormValid(false)
      return
    }
    props.setIsFormValid(productClasses.filter((x) => !x.designCompleted).length == 0)
  }, [productClasses])

  const onPostBenefitDesign = async () => {
    setShowConfirmModal(true)
    if (
      setTimeout(() => {
        setShowConfirmModal(false)
      }, 4000) &&
      !showConfirmModal
    ) {
      const result = await postBenefitDesignCommand(() =>
        finalizePerksBenefitDesign({
          planPeriodId: props.state.planPeriodId,
        }),
      )
      if (result.completed) {
        props.finish()
      } else {
        props.setIsSubmiting(false)
      }
    }
  }

  const onSubmit = async () => {
    Modal.confirm({
      title: translate('admin-dashboard.benefit-details.class-design.on-submit-alert-header'),
      content: (
        <span>
          <p>{translate('admin-dashboard.benefit-details.class-design.on-submit-alert-content-1')}</p>
          <p className="italic">
            {translate('admin-dashboard.benefit-details.class-design.on-submit-alert-content-2')}
          </p>
        </span>
      ),
      onOk() {
        onPostBenefitDesign()
      },
      onCancel() {
        props.setIsSubmiting(false)
      },
      okText: translate('admin-dashboard.benefit-details.class-design.delete-class-alert-ok-text'),
      okType: 'primary',
    })
  }

  const onProductClassWizardModalClosed = async () => {
    props.setIsFormValid(false)
    setProductClassModalState({
      ...productClassModalState,
      visible: false,
    })

    await reloadProductClasses().then(() => {
      setUpdateSummary(true)
    })
  }

  const onBenefitDesignClicked = (item: BenefitProductClassResponse) => {
    setProductClassModalState({
      productId: item.id,
      visible: true,
      editingStep: PerksProductClassWizard.SelectPerks,
      name: item.name,
    })
  }

  const onDeleteClassRequested = (productId: string) => {
    const found = productClasses.find((x) => x.id === productId)
    setDeleteProductClass(found)
  }

  const onDeleteClassExecuted = async (productId: string) => {
    const result = await sendDeleteProductClassCommand(() => removePerksProductClass(productId))
    if (result.completed) {
      reloadProductClasses().then(() => {
        setDeleteProductClass(null)
      })
    }
  }

  return (
    <>
      <global.components.dataDisplay.DeleteConfirmModal
        title={`Deleting '${deleteProductClass?.name}' ...`}
        content={'Are you sure you want to delete this class? This action cannot be undone.'}
        onDelete={async () => {
          await onDeleteClassExecuted(deleteProductClass?.id)
        }}
        onCancel={() => {
          setDeleteProductClass(null)
        }}
        deleteButtonLoading={isSendDeleteProductClassCommandLoading}
        visible={!!deleteProductClass}
      />

      {productClassModalState.visible && (
        <ProductClassWizardModal
          navigationMapping={PerksProductClassWizardNavItemsMapping}
          getProductClassWizard={(planPeriodId, productId) => getPerksProductClassDesignWizard(productId)}
          getProductClassWizardState={(planPeriodId, productId) => getPerksProductClassDesignWizardState(productId)}
          visible={productClassModalState.visible}
          id={productClassModalState.productId}
          planPeriodId={props.state.planPeriodId}
          afterClose={async () => await onProductClassWizardModalClosed()}
          editingStep={productClassModalState.editingStep}
          finishWizardMessage={`${translate(
            'admin-dashboard.benefit-details.perks-benefit-create-eligible-classes.finish-popup-class-design-wizard-message',
          ).replace(':className', productClassModalState.name)}`}
        />
      )}

      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={props.state}
        onSubmit={onSubmit}
        validate={(values) => {
          props.hasDirtyData(true)
          props.setIsFormValid(productClasses.length > 0)
          return {}
        }}
      >
        {(formik) => {
          const { handleSubmit, submitForm } = formik
          bindSubmitFormHandler(submitForm)
          return (
            <form onSubmit={handleSubmit} autoComplete="off" className={'perks-benefit-design-wrapper'}>
              <Row className={'mb-32'}>
                <Col span={12} className={'benefit_design_page_header'}>
                  <h5>{translate('admin-dashboard.benefit-details.perks-benefit-design.header')}</h5>
                  <h6 className={'inline mr-8'}>
                    {translate('admin-dashboard.benefit-details.perks-benefit-your-classes-next.subheader')}
                  </h6>
                </Col>
              </Row>
              <Skeleton loading={areProductClassesLoading}>
                {productClasses && (
                  <Row>
                    <Col span={24} className={'benefit_design_page_header'}>
                      <h5 className={'mt-0'}>{`${translate(
                        'admin-dashboard.benefit-details.perks-benefit-design.your-classes',
                      )}: ${productClasses.length}`}</h5>
                      {productClasses.map((item, index) => (
                        <ProductClassDetailsOnBenefitDesign
                          key={item.id}
                          collapseKey={index}
                          item={item}
                          onEdit={() => onBenefitDesignClicked(item)}
                          update={updateSummary}
                          setUpdate={setUpdateSummary}
                          onDelete={() => onDeleteClassRequested(item.id)}
                          planPeriodId={props.state.planPeriodId}
                        />
                        // <ProductClassDetails
                        //   key={item.id}
                        //   collapseKey={index}
                        //   item={item}
                        //   onEdit={() => onBenefitDesignClicked(item)}
                        //   update={updateSummary}
                        //   setUpdate={setUpdateSummary}
                        //   onDelete={() => onDeleteClassRequested(item.id)}
                        //   planPeriodId={props.state.planPeriodId}
                        // />
                      ))}
                    </Col>
                  </Row>
                )}
              </Skeleton>
              {showConfirmModal && <FinishDesignSuccessModal />}
            </form>
          )
        }}
      </Formik>
    </>
  )
}

const formValidator = (translate: any) => Yup.object({})

export default PerksFinishBenefitDesign
